import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import ConfirmDialog from '../../../../components/ConfirmDialog/ConfirmDialog';
import { GetAdminProspectMessagesAPI, LogAdminProspectMessageAPI, SendAdminProspectWhatsAppAPI } from '../../../../api/AdminAPI';
import {
  CHANNEL_OPTIONS, MESSAGE_MAX_LENGTH, MESSAGE_VARIANTS, defaultMessage, defaultVariant, labelOf,
  whatsappLink, instagramLink, emailLink,
} from '../prospectOptions';

const OPEN_LABELS = { whatsapp: 'Abrir no WhatsApp', instagram: 'Copiar e abrir o Instagram', email: 'Abrir o e-mail' };
const SEND_GABRIELA_LABEL = 'Enviar pelo WhatsApp da Gabriela';
const WHATSAPP_VIAS = [
  { value: 'gabriela', label: 'Pelo número da Gabriela' },
  { value: 'device', label: 'Neste aparelho' },
];
// Resposta do contato (gravada pelo webhook do número da Gabriela) fica destacada
const OUTBOUND_ROW_SX = { p: 1.5, borderRadius: '8px', bgcolor: '#F8F9FC', border: '1px solid #E8ECF0' };
const INBOUND_ROW_SX = { p: 1.5, borderRadius: '8px', bgcolor: '#EEF7EE', border: '1px solid #CDE7CF' };
const HINTS = {
  whatsapp: 'Abre a conversa no WhatsApp deste computador com o texto pronto. Envie com calma, sem disparar vários seguidos.',
  whatsappGabriela: 'Sai na hora pelo número da Gabriela. Até 30 por dia, com alguns minutos entre um envio e outro.',
  instagram: 'O Instagram não aceita texto pronto: a mensagem vai para a área de transferência, é só colar no direct.',
  email: 'Abre o seu programa de e-mail com o texto pronto.',
};

/**
 * 'Y-m-d H:i:s' -> "16/10/2026 10:00".
 * @param {string} value
 * @returns {string}
 */
function fmtDateTime(value) {
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * @param {Object|null} prospect
 * @returns {string[]} canais que o contato tem
 */
function availableChannels(prospect) {
  if (!prospect) return [];
  return [
    prospect.whatsapp && 'whatsapp',
    prospect.instagram && 'instagram',
    prospect.email && 'email',
  ].filter(Boolean);
}

/**
 * ProspectMessageDialog - Escreve a mensagem, abre no aparelho do admin e registra o envio.
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Object|null} props.prospect
 * @param {Function} props.onLogged recebe o contato atualizado
 * @param {Function} props.onCancel
 */
function ProspectMessageDialog({ isOpen, prospect, onLogged, onCancel }) {
  const channels = useMemo(() => availableChannels(prospect), [prospect]);
  const [channel, setChannel] = useState('');
  const [via, setVia] = useState('gabriela');
  const [variant, setVariant] = useState('conversa');
  const [message, setMessage] = useState('');
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !prospect) return;
    const initialVariant = defaultVariant(prospect);
    setChannel(channels[0] || '');
    setVia('gabriela');
    setVariant(initialVariant);
    setMessage(defaultMessage(prospect, initialVariant));
    setError('');
    setHistory([]);
    setLoadingHistory(true);
    GetAdminProspectMessagesAPI(prospect.id, (response) => {
      setLoadingHistory(false);
      if (response?.success) setHistory(response.items || []);
    });
  }, [isOpen, prospect, channels]);

  const handleVariantChange = (value) => {
    if (!value) return;
    setVariant(value);
    setMessage(defaultMessage(prospect, value));
  };

  const finish = (response, fallbackError) => {
    setSaving(false);
    if (!response?.success) {
      setError(response?.error || fallbackError);
      return;
    }
    onLogged(response.item);
  };

  const sendViaGabriela = channel === 'whatsapp' && via === 'gabriela';

  const handleConfirm = async () => {
    const text = message.trim();
    if (sendViaGabriela) {
      setSaving(true);
      setError('');
      SendAdminProspectWhatsAppAPI({ prospect_id: prospect.id, message: text, variant }, (response) => {
        finish(response, 'Não foi possível enviar a mensagem.');
      });
      return;
    }

    let link = null;
    if (channel === 'whatsapp') link = whatsappLink(prospect.whatsapp, text);
    if (channel === 'email') link = emailLink(prospect.email, text);
    if (channel === 'instagram') {
      link = instagramLink(prospect.instagram);
      try {
        await navigator.clipboard.writeText(text);
      } catch (e) {
        setError('Não consegui copiar o texto. Copie à mão antes de abrir o Instagram.');
        return;
      }
    }
    if (!link) return;

    window.open(link, '_blank', 'noopener');

    setSaving(true);
    setError('');
    LogAdminProspectMessageAPI({ prospect_id: prospect.id, channel, message: text, variant }, (response) => {
      finish(response, 'A conversa abriu, mas o registro do envio falhou.');
    });
  };

  return (
    <ConfirmDialog
      isOpen={isOpen}
      title={prospect ? `Mensagem para ${prospect.name}` : 'Mensagem'}
      variant="info"
      confirmLabel={sendViaGabriela ? SEND_GABRIELA_LABEL : (OPEN_LABELS[channel] || 'Abrir')}
      cancelLabel="Fechar"
      onConfirm={handleConfirm}
      onCancel={onCancel}
      confirmDisabled={saving || !channel || message.trim() === ''}
      loading={saving}
      maxWidth={600}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {channels.length === 0 ? (
          <Alert severity="warning">Este contato não tem WhatsApp, Instagram nem e-mail. Edite o contato antes.</Alert>
        ) : (
          <Box>
            <ToggleButtonGroup value={channel} exclusive size="small" onChange={(e, value) => value && setChannel(value)} disabled={saving}>
              {CHANNEL_OPTIONS.filter((item) => channels.includes(item.value)).map((item) => (
                <ToggleButton key={item.value} value={item.value} sx={{ px: 2, textTransform: 'none' }}>
                  {item.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {channel === 'whatsapp' && (
              <ToggleButtonGroup value={via} exclusive size="small" onChange={(e, value) => value && setVia(value)} disabled={saving} sx={{ ml: 1 }}>
                {WHATSAPP_VIAS.map((item) => (
                  <ToggleButton key={item.value} value={item.value} sx={{ px: 2, textTransform: 'none' }}>
                    {item.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {sendViaGabriela ? HINTS.whatsappGabriela : HINTS[channel]}
            </Typography>
          </Box>
        )}

        <Box>
          <ToggleButtonGroup value={variant} exclusive size="small" onChange={(e, value) => handleVariantChange(value)} disabled={saving}>
            {MESSAGE_VARIANTS.map((item) => (
              <ToggleButton key={item.value} value={item.value} sx={{ px: 2, textTransform: 'none' }}>
                {item.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            A primeira mensagem alterna os dois textos do teste A/B; quem já recebeu abre no Retorno. Trocar reescreve a mensagem.
          </Typography>
        </Box>

        <TextField
          label="Mensagem"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          fullWidth
          multiline
          minRows={6}
          maxRows={14}
          disabled={saving}
          inputProps={{ maxLength: MESSAGE_MAX_LENGTH }}
          helperText="Fica registrado o texto, o canal, a versão do teste e a hora."
        />

        {error && <Alert severity="error">{error}</Alert>}

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Histórico
          </Typography>
          {loadingHistory ? (
            <CircularProgress size={20} />
          ) : history.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Nenhuma mensagem ainda.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 220, overflowY: 'auto' }}>
              {history.map((item) => (
                <Box key={item.id} sx={item.via === 'inbound' ? INBOUND_ROW_SX : OUTBOUND_ROW_SX}>
                  <Typography variant="caption" color="text.secondary">
                    {fmtDateTime(item.created_at)}
                    {item.via === 'inbound' ? ' · Resposta recebida' : ` · ${labelOf(CHANNEL_OPTIONS, item.channel)}`}
                    {item.via === 'gabriela' && ' pelo número da Gabriela'}
                    {item.variant && ` · ${labelOf(MESSAGE_VARIANTS, item.variant)}`}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{item.message}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </ConfirmDialog>
  );
}

export default ProspectMessageDialog;
